import React from "react";
import { 
  FaBrain, 
  FaRobot, 
  FaChartLine, 
  FaEye, 
  FaLanguage, 
  FaCogs,
  FaDatabase,
  FaServer,
  FaCloud,
  FaCode,
  FaCheck,
  FaArrowRight,
  FaExclamationTriangle
} from "react-icons/fa";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import aiBg from "../../assets/ai-ml.webp"; // Use an appropriate background image

const AIMLServices = () => {
  // Services for the first 3-column section
  const services = [
    {
      icon: FaBrain,
      title: "Machine Learning",
      description: "Custom ML models for prediction, classification, and recommendation systems",
      image: "🤖"
    },
    {
      icon: FaChartLine,
      title: "Predictive Analytics",
      description: "Data-driven insights and forecasting for business decision making",
      image: "📈"
    },
    {
      icon: FaEye,
      title: "Computer Vision",
      description: "Image recognition, object detection, and video analysis solutions",
      image: "👁️"
    },
    {
      icon: FaLanguage,
      title: "NLP Solutions",
      description: "Chatbots, sentiment analysis, and language processing capabilities",
      image: "💬"
    },
    {
      icon: FaRobot,
      title: "AI Automation",
      description: "Intelligent process automation and workflow optimization",
      image: "⚙️"
    },
    {
      icon: FaCogs,
      title: "MLOps",
      description: "End-to-end machine learning lifecycle management and deployment",
      image: "🚀"
    }
  ];

  // Challenges for the second 3-column section
  const challenges = [
    {
      name: "Data Quality & Preparation",
      description: "Cleaning, labeling, and structuring data for effective model training.",
      complexity: "High",
      icon: FaDatabase
    },
    {
      name: "Model Deployment",
      description: "Moving from experimentation to scalable, reliable production systems.",
      complexity: "High",
      icon: FaServer
    },
    {
      name: "Infrastructure Cost",
      description: "Managing the expense of computational resources for training and inference.",
      complexity: "Medium",
      icon: FaCloud
    },
    {
      name: "Talent Gap",
      description: "Finding and retaining skilled data scientists and ML engineers.",
      complexity: "High",
      icon: FaBrain
    },
    {
      name: "Ethical AI & Bias",
      description: "Ensuring models are fair, transparent, and free from unintended bias.",
      complexity: "Critical",
      icon: FaExclamationTriangle
    },
    {
      name: "Integration with Legacy Systems",
      description: "Connecting modern AI solutions with existing business IT infrastructure.",
      complexity: "Medium",
      icon: FaCogs
    }
  ];

  // Solutions for the 2-column detailed section
  const solutions = [
    {
      area: "Model Development",
      measures: ["Custom Algorithm Design", "Hyperparameter Tuning", "Model Validation", "Experiment Tracking", "Feature Engineering", "A/B Testing"],
      icon: FaCode
    },
    {
      area: "Data Engineering",
      measures: ["Data Pipeline Creation", "Feature Store Management", "Data Versioning", "Quality Monitoring", "Automated Labeling", "Privacy Compliance"],
      icon: FaDatabase
    },
    {
      area: "MLOps & Deployment",
      measures: ["CI/CD for ML", "Containerization (Docker)", "Orchestration (Kubernetes)", "Model Serving", "Performance Monitoring", "Automated Retraining"],
      icon: FaCogs
    },
    {
      area: "Cloud AI Platforms",
      measures: ["AWS SageMaker", "Google Vertex AI", "Azure Machine Learning", "Managed Kubernetes", "Serverless Inference", "Cost Optimization Tools"],
      icon: FaCloud
    }
  ];

  return (
    <div className="bg-gray-50">
      <Navbar />
      <div className="min-h-screen">
        {/* Hero Section - Match Cybersecurity exactly */}
        <div
          className="relative bg-cover bg-center bg-no-repeat text-white py-24 overflow-hidden"
          style={{ backgroundImage: `url(${aiBg})` }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-bl from-blue-500 to-transparent opacity-20 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-blue-400 to-transparent opacity-20 rounded-full translate-y-1/2 -translate-x-1/2"></div>

          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-6xl font-bold mb-6 bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent pb-3">
                AI & ML Services
              </h1>
              <p className="text-xl text-blue-100 mb-10 leading-relaxed max-w-3xl mx-auto">
                Transform your business with intelligent AI solutions. From predictive analytics to automation, we help you harness the power of artificial intelligence.
              </p>
            </div>
          </div>
        </div>
        
        {/* Services Section - 3-column layout */}
        <div className="pt-10 pb-20 bg-white relative">
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-blue-50 to-transparent"></div>
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Our AI & ML Services
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Comprehensive artificial intelligence and machine learning solutions to drive innovation and business growth
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 hover:shadow-2xl transition-all duration-500 border border-blue-100 hover:border-blue-200 hover:transform hover:-translate-y-2"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-4xl bg-gradient-to-r from-blue-600 to-blue-800 p-4 rounded-2xl text-white">
                      {service.image}
                    </div>
                    <div className="text-blue-600">
                      <service.icon className="text-3xl opacity-80" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Challenges Section - Replaces "Threats", uses 3-column layout */}
        <div className="pt-10 pb-20 bg-gradient-to-br from-blue-50 to-gray-100 relative">
          <div className="absolute inset-0 bg-blue-900 opacity-5"></div>
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Common AI/ML Challenges
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Addressing key hurdles in implementing effective machine learning systems
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {challenges.map((challenge, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-blue-100 hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="flex items-center mb-4">
                    <div className={`p-3 rounded-xl mr-4 ${
                      challenge.complexity === 'Critical' ? 'bg-red-100 text-red-600' :
                      challenge.complexity === 'High' ? 'bg-orange-100 text-orange-600' :
                      'bg-yellow-100 text-yellow-600'
                    }`}>
                      <challenge.icon className="text-xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{challenge.name}</h3>
                      <span className={`text-sm font-semibold ${
                        challenge.complexity === 'Critical' ? 'text-red-600' :
                        challenge.complexity === 'High' ? 'text-orange-600' :
                        'text-yellow-600'
                      }`}>
                        Complexity: {challenge.complexity}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {challenge.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Solutions Section - 2-column detailed layout */}
        <div className="pt-10 pb-20 bg-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50 rounded-full -translate-y-48 translate-x-48"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-50 rounded-full translate-y-48 -translate-x-48"></div>
          
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Our AI/ML Solutions
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Comprehensive tools and methodologies to build, deploy, and manage AI systems
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {solutions.map((solution, index) => (
                <div key={index} className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
                  <div className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-2xl p-8 h-full transform group-hover:scale-105 transition-all duration-500">
                    <div className="flex items-center mb-6">
                      <div className="bg-white bg-opacity-20 p-3 rounded-xl mr-4">
                        <solution.icon className="text-2xl" />
                      </div>
                      <h3 className="text-2xl font-bold">{solution.area}</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {solution.measures.map((measure, idx) => (
                        <div key={idx} className="flex items-center text-blue-100">
                          <FaCheck className="text-green-300 mr-2 text-sm" />
                          <span className="text-sm">{measure}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="relative pt-10 pb-10 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-black opacity-10"></div>
          <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-blue-400 opacity-10 rounded-full -translate-y-1/4 translate-x-1/4"></div>
          <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-blue-300 opacity-10 rounded-full translate-y-1/4 -translate-x-1/4"></div>
          
          <div className="container mx-auto px-6 text-center relative z-10">
            <h2 className="text-5xl font-bold text-white mb-8">
              Ready to Transform with AI?
            </h2>
            <p className="text-xl text-blue-100 mb-12 max-w-3xl mx-auto leading-relaxed">
              Let us help you implement intelligent AI solutions that drive real business value. 
              From concept to deployment, we'll guide you through every step of your AI journey.
            </p>
            <div className="flex justify-center items-center mt-6">
              <button 
                onClick={() => (window.location.href = "/contact-us")}
                className="group bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-12 py-5 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl flex items-center gap-3"
              >
                Start AI Transformation
                <FaArrowRight className="group-hover:translate-x-2 transition-transform duration-200" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AIMLServices;